import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../api/client'
import { useRoles, type RoleView } from '../../api/roles'
import { useLocale } from '../../contexts/LocaleContext'
import { getLocalizedName } from '../../lib/localization'
import { rolesForKind, type MemberKind } from '../../lib/member-kinds'
import { AssetPicker, isAssetPickerEscapeToClear } from '../AssetPicker'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'

/**
 * A contact's sign-in access (spec 2026-10-06-06). A resident or a provider
 * signs in as their own contact row: "Invite to sign in" makes the contact a
 * member of that kind, with a grant on an asset, and mails a sign-in link. No
 * account is created.
 */
export interface ContactSignInAccess {
    active: boolean
    kind?: MemberKind
    role_code?: string
    email?: string
    link_sent?: boolean
}

const accessKey = (org: string, userUid: string) => ['contact-sign-in-access', org, userUid]

export function useContactSignInAccess(org: string, userUid: string, enabled: boolean) {
    return useQuery({
        queryKey: accessKey(org, userUid),
        enabled,
        queryFn: async (): Promise<ContactSignInAccess> => {
            const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/users/{userUid}/sign-in-access', {
                params: { path: { orgCode: org, userUid } },
            })
            if (error) throw new Error(JSON.stringify(error))
            return data as ContactSignInAccess
        },
    })
}

export function useInviteContactToSignIn(org: string, userUid: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: async (body: {
            kind: Exclude<MemberKind, 'staff'>
            grants?: { role_code: string; asset_uid: string }[]
            lang?: 'en' | 'fr'
        }): Promise<ContactSignInAccess> => {
            const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/users/{userUid}/sign-in-access', {
                params: { path: { orgCode: org, userUid } },
                body,
            })
            if (error) throw new Error(JSON.stringify(error))
            return data as ContactSignInAccess
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: accessKey(org, userUid) }),
    })
}

export function useRevokeContactSignInAccess(org: string, userUid: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: async () => {
            const { error } = await apiClient.DELETE('/api/v1/organizations/{orgCode}/users/{userUid}/sign-in-access', {
                params: { path: { orgCode: org, userUid } },
            })
            if (error) throw new Error(JSON.stringify(error))
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: accessKey(org, userUid) }),
    })
}

export function ContactSignInAccessDialog({
    org,
    userUid,
    open,
    onOpenChange,
    onInvited,
}: {
    org: string
    userUid: string
    open: boolean
    onOpenChange: (open: boolean) => void
    onInvited?: (access: ContactSignInAccess) => void
}) {
    const { t, i18n } = useTranslation('users')
    const { locale } = useLocale()
    const { data: rolesData } = useRoles(org, open)
    const roles = useMemo<RoleView[]>(() => rolesData?.data ?? [], [rolesData])
    const invite = useInviteContactToSignIn(org, userUid)

    const [kind, setKind] = useState<Exclude<MemberKind, 'staff'>>('resident')
    const [role, setRole] = useState('resident')
    const [assetUid, setAssetUid] = useState('')
    const [assetName, setAssetName] = useState('')
    const [error, setError] = useState<string | null>(null)

    const kindRoles = useMemo(() => {
        const fromApi = rolesForKind(kind, roles)
        if (fromApi.length > 0) return fromApi
        // Before GET /roles answers, the built-in codes of the kind.
        return (kind === 'resident' ? ['resident', 'property_owner'] : ['provider']).map((code) => ({ code } as RoleView))
    }, [kind, roles])

    useEffect(() => {
        if (open) {
            setKind('resident')
            setRole('resident')
            setAssetUid('')
            setAssetName('')
            setError(null)
        }
    }, [open])

    const roleLabel = (code: string) => {
        const found = roles.find((r) => r.code === code)
        return found ? getLocalizedName(found.name as Record<string, string>, locale) || code : code
    }

    const submit = async () => {
        setError(null)
        if (!assetUid) {
            setError(t('detail.sign_in_access.asset_required'))
            return
        }
        try {
            const access = await invite.mutateAsync({
                kind,
                grants: [{ role_code: role, asset_uid: assetUid }],
                lang: i18n.language?.startsWith('fr') ? 'fr' : 'en',
            })
            onOpenChange(false)
            onInvited?.(access)
        } catch (err) {
            setError(errorTitle(err) || t('detail.sign_in_access.failed'))
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="contact-sign-in-dialog"
                onEscapeKeyDown={(event) => {
                    if (isAssetPickerEscapeToClear(event)) event.preventDefault()
                }}
            >
                <DialogHeader>
                    <DialogTitle>{t('detail.sign_in_access.dialog_title')}</DialogTitle>
                    <DialogDescription>{t('detail.sign_in_access.dialog_description')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="contact-sign-in-kind">{t('detail.sign_in_access.kind')}</Label>
                        <Select
                            value={kind}
                            onValueChange={(value) => {
                                const next = value as Exclude<MemberKind, 'staff'>
                                setKind(next)
                                setRole(next === 'resident' ? 'resident' : 'provider')
                            }}
                        >
                            <SelectTrigger id="contact-sign-in-kind" data-testid="contact-sign-in-kind-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {(['resident', 'provider'] as const).map((k) => (
                                    <SelectItem key={k} value={k} data-testid={`contact-sign-in-kind-option-${k}`}>
                                        {t(`detail.sign_in_access.kinds.${k}`)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="contact-sign-in-role">{t('detail.sign_in_access.role')}</Label>
                        <Select value={role} onValueChange={setRole}>
                            <SelectTrigger id="contact-sign-in-role" data-testid="contact-sign-in-role-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {kindRoles.map((r) => (
                                    <SelectItem key={r.code} value={r.code} data-testid={`contact-sign-in-role-option-${r.code}`}>
                                        {roleLabel(r.code)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label>{t('detail.sign_in_access.asset')}</Label>
                        <AssetPicker
                            org={org}
                            value={assetUid}
                            valueLabel={assetName}
                            onChange={(uid, name) => {
                                setAssetUid(uid)
                                setAssetName(name)
                            }}
                            testId="contact-sign-in-asset-picker"
                            help={t('detail.sign_in_access.asset_help')}
                        />
                    </div>
                    {error && (
                        <p className="text-sm text-destructive" data-testid="contact-sign-in-error">
                            {error}
                        </p>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="contact-sign-in-cancel">
                        {t('common:actions.cancel')}
                    </Button>
                    <Button onClick={submit} disabled={invite.isPending} data-testid="contact-sign-in-submit">
                        {invite.isPending ? t('detail.sign_in_access.sending') : t('detail.sign_in_access.submit')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

function errorTitle(err: unknown): string {
    const text = err instanceof Error ? err.message : String(err ?? '')
    try {
        const parsed = JSON.parse(text)
        if (parsed && typeof parsed.title === 'string') return parsed.title
    } catch {
        // not JSON
    }
    return text
}
